import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Ruler, IndianRupee, Layers, Weight, Scissors, Package, Calendar, Factory, Truck, Search, X } from 'lucide-react';
import apiService from '../services/api';
import SearchableSelect from '../components/SearchableSelect';

const formatMachineRange = (machines: string[]): string => {
  if (!machines.length) return '-';
  const nums = machines.map(m => parseInt(m)).filter(n => !isNaN(n)).sort((a, b) => a - b);
  const nonNums = machines.filter(m => isNaN(parseInt(m)));
  if (!nums.length) return machines.join(', ');
  const ranges: string[] = [];
  let start = nums[0], end = nums[0];
  for (let i = 1; i < nums.length; i++) {
    if (nums[i] === end + 1) { end = nums[i]; }
    else { ranges.push(start === end ? `${start}` : `${start}-${end}`); start = nums[i]; end = nums[i]; }
  }
  ranges.push(start === end ? `${start}` : `${start}-${end}`);
  return [...ranges, ...nonNums].join(', ');
};

const fmt = (n: number) => n.toLocaleString('en-IN', { maximumFractionDigits: 2 });

const getTodayStr = () => new Date().toISOString().split('T')[0];
const getMonthStartStr = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
};

const Dashboard: React.FC = () => {
  const todayStr = getTodayStr();
  const [startDate, setStartDate] = useState(todayStr);
  const [endDate, setEndDate] = useState(todayStr);
  const [searchText, setSearchText] = useState('');
  const [qualityFilter, setQualityFilter] = useState<string[]>([]);
  const [colorFilter, setColorFilter] = useState<string[]>([]);

  const setToday = () => { const t = getTodayStr(); setStartDate(t); setEndDate(t); };
  const setMonth = () => { setStartDate(getMonthStartStr()); setEndDate(getTodayStr()); };

  const isToday = startDate === todayStr && endDate === todayStr;
  const isMonth = startDate === getMonthStartStr() && endDate === todayStr;

  const { data: dashboardData, isLoading: dashboardLoading } = useQuery({
    queryKey: ['takaDashboard', startDate, endDate],
    queryFn: () => apiService.get(`/taka-inventory/today/dashboard?startDate=${startDate}&endDate=${endDate}`),
    refetchInterval: 30000
  });

  const { data: settingsData, isLoading: settingsLoading } = useQuery({
    queryKey: ['adminSettings'],
    queryFn: async () => (await apiService.get('/admin-settings')).settings
  });

  const isLoading = dashboardLoading || settingsLoading;
  const qualities = dashboardData?.qualities || [];
  const rate = settingsData?.pickRate ?? 0.07;

  // Derive filter options from data
  const qualityOptions: string[] = useMemo(() =>
    Array.from(new Set(qualities.map((q: any) => q.quality) as string[])).sort(),
    [qualities]
  );
  const colorOptions: string[] = useMemo(() =>
    Array.from(new Set(
      qualities.flatMap((q: any) => (q.takas || []).map((t: any) => t.colour)).filter((c: string) => c && c !== '-') as string[]
    )).sort(),
    [qualities]
  );

  // Client-side filtering
  const filteredQualities = useMemo(() => qualities.filter((q: any) => {
    if (qualityFilter.length > 0 && !qualityFilter.includes(q.quality)) return false;
    if (colorFilter.length > 0) {
      const hasColor = (q.takas || []).some((t: any) => colorFilter.includes(t.colour));
      if (!hasColor) return false;
    }
    if (searchText) {
      const term = searchText.toLowerCase();
      const matchesQuality = q.quality.toLowerCase().includes(term);
      const matchesMachine = (q.machines || []).some((m: string) => m.toLowerCase().includes(term));
      const matchesColour = (q.takas || []).some((t: any) => (t.colour || '').toLowerCase().includes(term));
      if (!matchesQuality && !matchesMachine && !matchesColour) return false;
    }
    return true;
  }), [qualities, searchText, qualityFilter, colorFilter]);

  // Recompute totals from filtered data
  const filteredTotalMeters = filteredQualities.reduce((s: number, q: any) => s + q.totalMeters, 0);
  const filteredTotalSaree = filteredQualities.reduce((s: number, q: any) => s + q.totalSaree, 0);
  const filteredTotalWeight = filteredQualities.reduce((s: number, q: any) => s + q.totalWeight, 0);
  const filteredTakaCount = filteredQualities.reduce((s: number, q: any) => s + q.takaCount, 0);
  const filteredInStock = filteredQualities.reduce((s: number, q: any) => s + (q.inStock || 0), 0);
  const filteredDispatched = filteredQualities.reduce((s: number, q: any) => s + (q.dispatched || 0), 0);
  const filteredAmount = parseFloat(
    filteredQualities.reduce((sum: number, q: any) =>
      sum + (q.takas || []).reduce((s: number, t: any) => s + (t.meter || 0) * (t.avgPick || 0) * rate, 0), 0
    ).toFixed(2)
  );

  const hasFilters = searchText || qualityFilter.length > 0 || colorFilter.length > 0;

  if (isLoading) {
    return (
      <div className="p-3 sm:p-5 space-y-4 sm:space-y-5">
        <div className="animate-pulse space-y-4 sm:space-y-5">
          <div className="h-14 bg-gray-100 rounded-2xl"></div>
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="h-24 bg-gray-100 rounded-2xl"></div>)}
          </div>
          <div className="h-52 bg-gray-100 rounded-2xl"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-5 space-y-4 sm:space-y-5">

      {/* Header with Date Range */}
      <div className="bg-white rounded-2xl border border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-primary-600 flex items-center justify-center shadow-lg shadow-blue-600/20">
              <Factory className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Production Dashboard</h1>
              <div className="flex items-center gap-1.5">
                <Calendar className="w-3 h-3 text-gray-400" />
                <p className="text-xs text-gray-400">
                  {startDate === endDate
                    ? new Date(startDate + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' })
                    : `${new Date(startDate + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} - ${new Date(endDate + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`
                  }
                </p>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" />
            <span className="text-xs text-gray-400">to</span>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" />
            <div className="flex bg-gray-100 rounded-lg p-0.5 ml-1">
              <button onClick={setToday}
                className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-all duration-200 ${
                  isToday ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}>Today</button>
              <button onClick={setMonth}
                className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-all duration-200 ${
                  isMonth && !isToday ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}>Month</button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats - 6 cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        <div className="bg-white rounded-2xl border border-gray-200 p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
              <Ruler className="w-4 h-4 text-white" />
            </div>
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Total Meters</span>
          </div>
          <p className="text-xl sm:text-2xl font-extrabold text-gray-900 tabular-nums">{fmt(filteredTotalMeters)}</p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center">
              <IndianRupee className="w-4 h-4 text-white" />
            </div>
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Total Amount</span>
          </div>
          <p className="text-xl sm:text-2xl font-extrabold text-gray-900 tabular-nums">{fmt(filteredAmount)}</p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-violet-600 flex items-center justify-center">
              <Scissors className="w-4 h-4 text-white" />
            </div>
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Total Saree</span>
          </div>
          <p className="text-xl sm:text-2xl font-extrabold text-gray-900 tabular-nums">{fmt(filteredTotalSaree)}</p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center">
              <Weight className="w-4 h-4 text-white" />
            </div>
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Total Weight</span>
          </div>
          <p className="text-xl sm:text-2xl font-extrabold text-gray-900 tabular-nums">{fmt(filteredTotalWeight)}</p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-sky-500 flex items-center justify-center">
              <Package className="w-4 h-4 text-white" />
            </div>
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">In Stock</span>
          </div>
          <p className="text-xl sm:text-2xl font-extrabold text-cyan-700 tabular-nums">{filteredInStock} <span className="text-sm font-semibold text-gray-400">/ {filteredTakaCount}</span></p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-rose-500 to-pink-500 flex items-center justify-center">
              <Truck className="w-4 h-4 text-white" />
            </div>
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Dispatched</span>
          </div>
          <p className="text-xl sm:text-2xl font-extrabold text-rose-700 tabular-nums">{filteredDispatched} <span className="text-sm font-semibold text-gray-400">/ {filteredTakaCount}</span></p>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="bg-white rounded-2xl border border-gray-200 px-4 sm:px-6 py-3 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-0 sm:min-w-[300px]">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input type="text" value={searchText} onChange={e => setSearchText(e.target.value)}
            placeholder="Search quality, machine, colour..."
            className="w-full pl-9 pr-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" />
        </div>
        <SearchableSelect value={qualityFilter} onChange={setQualityFilter} options={qualityOptions} placeholder="All Qualities"
          className="px-3 py-1.5 text-sm border border-gray-200 rounded-xl bg-white focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-500 w-full sm:w-52 cursor-text flex items-center gap-1 flex-wrap min-h-[36px]" />
        <SearchableSelect value={colorFilter} onChange={setColorFilter} options={colorOptions} placeholder="All Colors"
          className="px-3 py-1.5 text-sm border border-gray-200 rounded-xl bg-white focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-500 w-full sm:w-52 cursor-text flex items-center gap-1 flex-wrap min-h-[36px]" />
        {hasFilters && (
          <button onClick={() => { setSearchText(''); setQualityFilter([]); setColorFilter([]); }}
            className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium">
            <X className="w-3 h-3" /> Clear
          </button>
        )}
      </div>

      {/* Running Qualities Table */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-600 to-primary-600 flex items-center justify-center">
              <Layers className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-gray-900">Running Qualities</h2>
              <p className="text-[11px] text-gray-400">
                {hasFilters
                  ? `${filteredQualities.length} of ${qualities.length} qualities`
                  : `${qualities.length} ${qualities.length === 1 ? 'quality' : 'qualities'} active`
                }
              </p>
            </div>
          </div>
        </div>

        {filteredQualities.length > 0 ? (
          <>
            {/* Mobile: stacked cards (the desktop table below is hidden on phones) */}
            <div className="sm:hidden divide-y divide-gray-100">
              {filteredQualities.map((q: any) => {
                const qualityAmount = parseFloat((q.takas || []).reduce((sum: number, t: any) => sum + (t.meter || 0) * (t.avgPick || 0) * rate, 0).toFixed(2));
                return (
                  <div key={q.quality} className="px-4 py-3">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-bold text-gray-900 truncate">{q.quality}</span>
                      <span className="text-sm font-extrabold text-blue-900 tabular-nums shrink-0">{fmt(qualityAmount)}</span>
                    </div>
                    <div className="mt-1.5">
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full">
                        <Factory className="w-3 h-3 text-gray-400 shrink-0" />
                        <span className="truncate max-w-[240px]">{formatMachineRange(q.machines || [])}</span>
                      </span>
                    </div>
                    <div className="mt-2 grid grid-cols-3 gap-2">
                      <div className="rounded-lg bg-gray-50 px-2 py-1.5 text-center">
                        <p className="text-[10px] text-gray-400 uppercase font-semibold leading-none">Taka</p>
                        <p className="text-sm font-bold text-gray-800 tabular-nums mt-1">{q.takaCount}</p>
                      </div>
                      <div className="rounded-lg bg-green-50 px-2 py-1.5 text-center">
                        <p className="text-[10px] text-green-500 uppercase font-semibold leading-none">Stock</p>
                        <p className="text-sm font-bold text-green-700 tabular-nums mt-1">{q.inStock || 0}</p>
                      </div>
                      <div className="rounded-lg bg-orange-50 px-2 py-1.5 text-center">
                        <p className="text-[10px] text-orange-500 uppercase font-semibold leading-none">Dispatch</p>
                        <p className="text-sm font-bold text-orange-700 tabular-nums mt-1">{q.dispatched || 0}</p>
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-gray-400">
                      <span>Meters <b className="text-gray-700 tabular-nums">{fmt(q.totalMeters)}</b></span>
                      <span>Saree <b className="text-gray-700 tabular-nums">{fmt(q.totalSaree)}</b></span>
                      <span>Weight <b className="text-gray-700 tabular-nums">{fmt(q.totalWeight)}</b></span>
                    </div>
                  </div>
                );
              })}
              {/* Grand total */}
              <div className="px-4 py-3 bg-blue-50/70">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold text-blue-900">Grand Total</span>
                  <span className="text-sm font-extrabold text-blue-900 tabular-nums">{fmt(filteredAmount)}</span>
                </div>
                <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-blue-700/70">
                  <span>Taka <b className="text-blue-900 tabular-nums">{filteredTakaCount}</b></span>
                  <span>Stock <b className="text-green-700 tabular-nums">{filteredInStock}</b></span>
                  <span>Dispatch <b className="text-orange-700 tabular-nums">{filteredDispatched}</b></span>
                  <span>Meters <b className="text-blue-900 tabular-nums">{fmt(filteredTotalMeters)}</b></span>
                  <span>Weight <b className="text-blue-900 tabular-nums">{fmt(filteredTotalWeight)}</b></span>
                </div>
              </div>
            </div>
            <div className="hidden sm:block overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-blue-600">
                  <th className="text-left py-3 sm:py-3.5 px-3 sm:px-5 text-xs sm:text-sm font-bold text-white">Quality</th>
                  <th className="text-left py-3 sm:py-3.5 px-3 sm:px-4 text-xs sm:text-sm font-bold text-white">Machines</th>
                  <th className="text-center py-3 sm:py-3.5 px-2 sm:px-4 text-xs sm:text-sm font-bold text-white">Taka</th>
                  <th className="text-center py-3 sm:py-3.5 px-2 sm:px-3 text-xs sm:text-sm font-bold text-white">Stock</th>
                  <th className="text-center py-3 sm:py-3.5 px-2 sm:px-3 text-xs sm:text-sm font-bold text-white">Dispatch</th>
                  <th className="text-right py-3 sm:py-3.5 px-2 sm:px-4 text-xs sm:text-sm font-bold text-white">Meters</th>
                  <th className="text-right py-3 sm:py-3.5 px-2 sm:px-4 text-xs sm:text-sm font-bold text-white">Saree</th>
                  <th className="text-right py-3 sm:py-3.5 px-2 sm:px-4 text-xs sm:text-sm font-bold text-white">Weight</th>
                  <th className="text-right py-3 sm:py-3.5 px-3 sm:px-5 text-xs sm:text-sm font-bold text-white">Amount</th>
                </tr>
              </thead>
              <tbody>
                {filteredQualities.map((q: any, idx: number) => {
                  const qualityAmount = parseFloat(
                    (q.takas || []).reduce((sum: number, t: any) => sum + (t.meter || 0) * (t.avgPick || 0) * rate, 0).toFixed(2)
                  );
                  return (
                    <tr key={q.quality} className={`border-b border-gray-100 hover:bg-blue-50/50 transition-colors ${idx % 2 !== 0 ? 'bg-gray-50/60' : 'bg-white'}`}>
                      <td className="py-4 px-5">
                        <span className="text-[13px] font-bold text-gray-900">{q.quality}</span>
                      </td>
                      <td className="py-4 px-4">
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-700 bg-gray-100 px-3 py-1 rounded-full">
                          <Factory className="w-3 h-3 text-gray-400" />
                          {formatMachineRange(q.machines || [])}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span className="text-[13px] font-bold text-gray-800 tabular-nums">{q.takaCount}</span>
                      </td>
                      <td className="py-4 px-3 text-center">
                        <span className="inline-flex min-w-[28px] justify-center text-xs font-bold text-green-700 bg-green-50 px-2.5 py-1 rounded-full tabular-nums">
                          {q.inStock || 0}
                        </span>
                      </td>
                      <td className="py-4 px-3 text-center">
                        <span className="inline-flex min-w-[28px] justify-center text-xs font-bold text-orange-700 bg-orange-50 px-2.5 py-1 rounded-full tabular-nums">
                          {q.dispatched || 0}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-right text-[13px] font-semibold text-gray-800 tabular-nums">{fmt(q.totalMeters)}</td>
                      <td className="py-4 px-4 text-right text-[13px] text-gray-600 tabular-nums">{fmt(q.totalSaree)}</td>
                      <td className="py-4 px-4 text-right text-[13px] text-gray-600 tabular-nums">{fmt(q.totalWeight)}</td>
                      <td className="py-4 px-5 text-right text-[13px] font-bold text-gray-900 tabular-nums">{fmt(qualityAmount)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-blue-50 border-t-2 border-blue-200">
                  <td className="py-4 px-5 text-[13px] font-bold text-blue-900">Grand Total</td>
                  <td className="py-4 px-4"></td>
                  <td className="py-4 px-4 text-center text-[13px] font-bold text-blue-900 tabular-nums">{filteredTakaCount}</td>
                  <td className="py-4 px-3 text-center text-[13px] font-bold text-green-700 tabular-nums">{filteredInStock}</td>
                  <td className="py-4 px-3 text-center text-[13px] font-bold text-orange-700 tabular-nums">{filteredDispatched}</td>
                  <td className="py-4 px-4 text-right text-[13px] font-bold text-blue-900 tabular-nums">{fmt(filteredTotalMeters)}</td>
                  <td className="py-4 px-4 text-right text-[13px] font-bold text-blue-900 tabular-nums">{fmt(filteredTotalSaree)}</td>
                  <td className="py-4 px-4 text-right text-[13px] font-bold text-blue-900 tabular-nums">{fmt(filteredTotalWeight)}</td>
                  <td className="py-4 px-5 text-right text-[13px] font-extrabold text-blue-900 tabular-nums">{fmt(filteredAmount)}</td>
                </tr>
              </tfoot>
            </table>
            </div>
          </>
        ) : (
          <div className="py-14 text-center">
            <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center mx-auto mb-3">
              <Layers className="w-6 h-6 text-gray-300" />
            </div>
            <p className="text-sm text-gray-400">{hasFilters ? 'No matching qualities' : 'No running qualities'}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
